import { pdfStatus } from '../../api/apiCallReport';
import actionConstants from '../constants/ActionConstants';

// Action types
export const REPORT_PDF_STATUS_CHECK_START = 'REPORT_PDF_STATUS_CHECK_START';
export const REPORT_PDF_STATUS_CHECK_STOP = 'REPORT_PDF_STATUS_CHECK_STOP';

// Action creators
export const updateReportPdfStatus = (newPayload) => ({
  type: actionConstants.REPORT_PDF_STATUS_UPDATE,
  payload: newPayload,
});

export const startReportPdfStatusCheck = () => ({
  type: REPORT_PDF_STATUS_CHECK_START,
});

export const stopReportPdfStatusCheck = () => ({
  type: REPORT_PDF_STATUS_CHECK_STOP,
});

const MAX_RETRIES = 6; // Maximum number of retries for fetching report details
const MAX_RETRIES_STOPED = 4; // Maximum number of retries for fetching report details
let reportTries = {};

// Store dispatch function for immediate polling
let currentDispatch = null;
let pollingIntervalMs = 5000; // Store interval duration

export const resetReportTries = (reportId) => {
  reportTries[reportId] = 0;
  // Trigger immediate check if polling is active
  if (currentDispatch && pollingInterval) {
    console.info(
      'Triggering immediate check after reset for report:',
      reportId
    );
    currentDispatch(checkReportPdfStatus());
    const currentInterval = pollingIntervalMs || 5000;
    clearInterval(pollingInterval);
    pollingInterval = setInterval(() => {
      currentDispatch(checkReportPdfStatus());
    }, currentInterval);
  }
};

const clearReportMaxTries = (dispatch, reportId) => {
  const currentTries = reportTries[reportId] || 0;
  if (currentTries >= MAX_RETRIES) {
    dispatch(
      updateReportPdfStatus({
        reportId,
        isPending: false,
        error: 'Max retries reached',
      })
    );
  }
};

const getReportById = async (
  reportId,
  type = 'pending',
  idsForPolling = []
) => {
  const MAX = type === 'pending' ? MAX_RETRIES : MAX_RETRIES_STOPED;
  if (reportTries[reportId] && reportTries[reportId] >= MAX) {
    return { _id: reportId, pollingType: type, error: 'Max retries reached' };
  }
  idsForPolling.push(reportId);
};

const validIsPending = (report, isBefore = false) => {
  if (!report) return;
  const { reportPDFDetails, updateHistory } = report;
  const { isPending, url, updatedAt: pdfDate } = reportPDFDetails || {};
  const { updatedAt: finishDate } = updateHistory[0] || {};
  const hasPDF = url && pdfDate && finishDate;
  const hasValidPDF = hasPDF && pdfDate.valueOf() > finishDate.valueOf();
  const exceededMaxRetries =
    reportTries[report._id] && reportTries[report._id] >= MAX_RETRIES;

  let isPendingNew = isPending;
  if (!url) {
    isPendingNew = true; // If there's no URL, we consider it pending
  }
  if (!hasValidPDF) {
    isPendingNew = true;
  }
  if ((!isBefore && hasValidPDF) || exceededMaxRetries) {
    isPendingNew = false;
  }
  return isPendingNew;
};

// Thunk action to check PDF status for reports in the store
export const checkReportPdfStatus = () => async (dispatch, getState) => {
  const { getReportList } = getState();

  // Get all reports that are currently in the "generating" status
  const generatingReports = [];
  const failedReports = [];
  const maxRetriesReports = []; // Track reports that exceeded max retries

  console.info('Checking report PDF status...', {
    generatingReports,
    getReportList,
  });

  if (getReportList?.reportData) {
    getReportList.reportData.forEach((report) => {
      // Check if report exceeded max retries BEFORE checking if it's pending
      const currentTries = reportTries[report._id] || 0;
      const hasExceededMaxRetries = currentTries >= MAX_RETRIES;

      if (hasExceededMaxRetries && validIsPending(report, true)) {
        // Report exceeded max retries but is still pending - mark for update
        maxRetriesReports.push(report._id);
        console.info(
          `Report ${report._id} exceeded max retries (${currentTries}/${MAX_RETRIES})`
        );
      } else if (validIsPending(report, true) && !hasExceededMaxRetries) {
        // Report is pending and hasn't exceeded max retries
        generatingReports.push(report._id);
      }
    });
  }

  // Handle reports that exceeded max retries - update them immediately
  if (maxRetriesReports.length > 0) {
    maxRetriesReports.forEach((reportId) => {
      clearReportMaxTries(dispatch, reportId);
    });
  }

  // If no reports are generating, don't make the API call
  if (generatingReports.length === 0 && failedReports.length === 0) {
    return Promise.resolve();
  }

  console.info('Generating reports:', generatingReports);
  console.info('Failed reports:', failedReports);

  const idsForPolling = [];

  // Promise.all each generating report to get the status
  generatingReports.forEach((reportId) =>
    getReportById(reportId, 'pending', idsForPolling)
  );
  failedReports.forEach((reportId) =>
    getReportById(reportId, 'failed', idsForPolling)
  );

  // Only make API call if there are IDs to check
  if (idsForPolling.length === 0) {
    return Promise.resolve();
  }

  const response = pdfStatus(idsForPolling);
  response
    .then((response) => {
      const reports = response?.body?.data || [];
      console.info('Reports:', reports);

      // Track which reports were returned from API
      const returnedReportIds = new Set(reports.map((r) => r._id));

      // Update each report's status in the Redux store
      reports.forEach((report) => {
        if (!report) return;
        const { _id } = report;

        // Increment retry count for this report
        reportTries[_id] = (reportTries[_id] || 0) + 1;
        const currentTries = reportTries[_id];

        if (report.error) {
          dispatch(updateReportPdfStatus({ reportId: _id, isPending: false }));
          return;
        }

        const { reportPDFDetails } = report;
        const { url, updatedAt: pdfDate } = reportPDFDetails || {};

        const isPendingNew = validIsPending(report);
        let urlNew;
        let urlDate;

        if (!isPendingNew && url) {
          urlNew = url;
          urlDate = pdfDate;
        }

        // Check if this report exceeded max retries after this attempt
        const exceededMaxRetries = currentTries >= MAX_RETRIES;

        const newPayload = {
          reportId: report._id,
          isPending: exceededMaxRetries ? false : isPendingNew, // Force false if exceeded max retries
          url: urlNew,
          updatedAt: urlDate,
        };

        if (exceededMaxRetries) {
          newPayload.error = 'Max retries reached';
        }

        dispatch(updateReportPdfStatus(newPayload));
      });

      // Handle reports that were requested but not returned by API
      // This can happen if there are network issues or server errors
      idsForPolling.forEach((requestedId) => {
        if (!returnedReportIds.has(requestedId)) {
          // Increment retry count for reports that weren't returned
          reportTries[requestedId] = (reportTries[requestedId] || 0) + 1;
          clearReportMaxTries(dispatch, requestedId);
        }
      });
    })
    .catch((error) => {
      console.error('Error fetching reports:', error);

      // Handle all requested reports as failed if API call fails
      idsForPolling.forEach((reportId) => {
        reportTries[reportId] = (reportTries[reportId] || 0) + 1;
        clearReportMaxTries(dispatch, reportId);
      });
    });

  return Promise.resolve();
};

// Thunk action to start polling
let pollingInterval = null;

export const startPolling =
  (intervalMs = 5000) =>
  (dispatch) => {
    console.info('startPolling');
    reportTries = {};

    // Store dispatch and interval duration for use in resetReportTries
    currentDispatch = dispatch;
    pollingIntervalMs = intervalMs;

    // Stop any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Start new polling
    dispatch(startReportPdfStatusCheck());

    // Immediately check status
    dispatch(checkReportPdfStatus());

    // Set up interval for future checks
    pollingInterval = setInterval(() => {
      dispatch(checkReportPdfStatus());
    }, intervalMs);
  };

export const stopPolling = () => (dispatch) => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  // Clear dispatch reference
  currentDispatch = null;
  dispatch(stopReportPdfStatusCheck());
};
